import React from 'react';
import { Link } from 'react-router-dom';
import './EmptyState.css';

// Shared illustration — abstract node/connection graphic
const Illustration = ({ variant }) => (
  <svg
    className="es-illustration"
    viewBox="0 0 200 140"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Background ring */}
    <circle cx="100" cy="70" r="54" stroke="#F17501" strokeWidth="1" strokeDasharray="4 4" opacity="0.18" />
    <circle cx="100" cy="70" r="38" stroke="#F17501" strokeWidth="1" opacity="0.08" />

    {variant === 'saved' ? (
      <>
        {/* Bookmark shape */}
        <path
          d="M80 34 h40 a4 4 0 0 1 4 4 v58 l-24 -14 -24 14 V38 a4 4 0 0 1 4 -4z"
          fill="#FFF4E8"
          stroke="#F17501"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        {/* Inner accent line */}
        <line x1="88" y1="50" x2="112" y2="50" stroke="#F17501" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
        <line x1="88" y1="58" x2="105" y2="58" stroke="#F17501" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />

        {/* Dot sparkles */}
        <circle cx="60" cy="44" r="3" fill="#F17501" opacity="0.18" />
        <circle cx="144" cy="38" r="2.5" fill="#F17501" opacity="0.22" />
        <circle cx="52" cy="90" r="2" fill="#F17501" opacity="0.14" />
        <circle cx="148" cy="96" r="3" fill="#F17501" opacity="0.16" />
        <circle cx="100" cy="118" r="2.5" fill="#F17501" opacity="0.13" />
      </>
    ) : variant === 'vision' ? (
      <>
        {/* Center node — big */}
        <circle cx="100" cy="70" r="14" fill="#FFF4E8" stroke="#F17501" strokeWidth="2" />
        <circle cx="100" cy="70" r="6" fill="#F17501" />

        {/* Orbiting nodes */}
        <circle cx="100" cy="24" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="100" cy="24" r="3" fill="#F17501" opacity="0.5" />

        <circle cx="148" cy="52" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="148" cy="52" r="3" fill="#F17501" opacity="0.5" />

        <circle cx="148" cy="96" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="148" cy="96" r="3" fill="#F17501" opacity="0.4" />

        <circle cx="52" cy="52" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="52" cy="52" r="3" fill="#F17501" opacity="0.4" />

        <circle cx="52" cy="96" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="52" cy="96" r="3" fill="#F17501" opacity="0.3" />

        {/* Connecting lines */}
        <line x1="100" y1="56" x2="100" y2="31" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <line x1="113" y1="62" x2="141" y2="55" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <line x1="113" y1="78" x2="141" y2="93" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <line x1="87" y1="62" x2="59" y2="55" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <line x1="87" y1="78" x2="59" y2="93" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />

        {/* Tiny accent dots */}
        <circle cx="100" cy="116" r="3" fill="#F17501" opacity="0.2" />
        <circle cx="30" cy="70" r="3" fill="#F17501" opacity="0.15" />
        <circle cx="170" cy="70" r="3" fill="#F17501" opacity="0.15" />
      </>
    ) : (
      <>
        {/* Side quest: diverging paths / arrows */}
        {/* Central dot */}
        <circle cx="100" cy="70" r="10" fill="#FFF4E8" stroke="#F17501" strokeWidth="2" />
        <circle cx="100" cy="70" r="4" fill="#F17501" />

        {/* Left branch */}
        <path d="M91 65 Q70 48 52 42" stroke="#F17501" strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" opacity="0.5" />
        <circle cx="50" cy="40" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="50" cy="40" r="3" fill="#F17501" opacity="0.5" />

        {/* Right branch */}
        <path d="M109 65 Q130 48 148 42" stroke="#F17501" strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" opacity="0.5" />
        <circle cx="150" cy="40" r="7" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" />
        <circle cx="150" cy="40" r="3" fill="#F17501" opacity="0.5" />

        {/* Down branch */}
        <path d="M100 80 Q100 95 100 108" stroke="#F17501" strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" opacity="0.35" />
        <circle cx="100" cy="112" r="6" fill="#FFF4E8" stroke="#F17501" strokeWidth="1.5" opacity="0.6" />

        {/* Far left branch */}
        <path d="M43 43 Q32 52 28 66" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.25" strokeLinecap="round" />
        <circle cx="26" cy="70" r="4" fill="#F17501" opacity="0.15" />

        {/* Far right branch */}
        <path d="M157 43 Q168 52 172 66" stroke="#F17501" strokeWidth="1" strokeDasharray="3 3" opacity="0.25" strokeLinecap="round" />
        <circle cx="174" cy="70" r="4" fill="#F17501" opacity="0.15" />

        {/* Sparkle dots */}
        <circle cx="70" cy="22" r="2" fill="#F17501" opacity="0.3" />
        <circle cx="130" cy="22" r="2" fill="#F17501" opacity="0.25" />
        <circle cx="20" cy="40" r="2" fill="#F17501" opacity="0.2" />
      </>
    )}
  </svg>
);

const EmptyState = ({ variant, title, description, ctaLabel, ctaTo }) => (
  <div className="es-wrap">
    <div className="es-visual">
      <Illustration variant={variant} />
    </div>
    <h3 className="es-title">{title}</h3>
    <p className="es-desc">{description}</p>
    <Link to={ctaTo} className="es-cta">
      {ctaLabel}
    </Link>
  </div>
);

export default EmptyState;
